// The Claims page's "Lists" strip: the practice's open claim lists (title, count, last
// updated, a link), with the closed ones behind a toggle. Hidden entirely while the
// feature is not available (the table's migration has not run: 503) or there are no lists.
import React, { useEffect, useState } from "react";
import "./ClaimLists.css";
import { fetchClaimLists, listPath } from "./api";

export function formatUpdated(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function ClaimListsStrip({ practiceId, refreshKey = 0, onOpenList }) {
  const [lists, setLists] = useState(null);
  const [error, setError] = useState(null);
  const [showClosed, setShowClosed] = useState(false);

  useEffect(() => {
    if (!practiceId) return undefined;
    let live = true;
    setError(null);
    fetchClaimLists(practiceId)
      .then((rows) => {
        if (live) setLists(rows);
      })
      .catch((err) => {
        if (!live) return;
        setLists([]);
        // 503 is "not available yet": nothing to say. Anything else is worth a line.
        if (err.status !== 503) setError(err.message);
      });
    return () => {
      live = false;
    };
  }, [practiceId, refreshKey]);

  if (error) {
    return (
      <div className="claim-lists-strip" data-testid="claim-lists-strip">
        <span className="claim-lists-label">Lists</span>
        <span className="claim-lists-error">{error}</span>
      </div>
    );
  }
  if (!lists || lists.length === 0) return null;

  const open = lists.filter((l) => l.status === "open");
  const closed = lists.filter((l) => l.status === "closed");
  const shown = showClosed ? [...open, ...closed] : open;

  const go = (e, list) => {
    if (!onOpenList) return;
    e.preventDefault();
    onOpenList(list.id);
  };

  return (
    <div className="claim-lists-strip" data-testid="claim-lists-strip">
      <span className="claim-lists-label">Lists</span>
      {shown.length === 0 ? (
        <span className="claim-lists-none">No open lists.</span>
      ) : (
        shown.map((l) => (
          <a
            key={l.id}
            href={listPath(practiceId, l.id)}
            onClick={(e) => go(e, l)}
            className={`claim-list-chip${l.status === "closed" ? " closed" : ""}`}
            title={`Last updated ${formatUpdated(l.updated_at)}`}
            data-testid="claim-list-chip"
          >
            <span className="claim-list-chip-title">{l.title}</span>
            <span className="claim-list-count">{l.count}</span>
            <span className="claim-list-chip-updated">{formatUpdated(l.updated_at)}</span>
          </a>
        ))
      )}
      {closed.length > 0 && (
        <button
          type="button"
          className="btn-link claim-lists-toggle"
          onClick={() => setShowClosed((v) => !v)}
        >
          {showClosed ? "Hide closed" : `Show closed (${closed.length})`}
        </button>
      )}
    </div>
  );
}
